import React, { useCallback, useEffect, useState } from 'react';
import useTranslate from '../../../../../../Hooks/useTranslate';
import { useAtomValue, useAtom, useSetAtom } from 'jotai';
import useAPI from '../../../../../../api';
import { Tabs, Tab, Paper, Fade, Divider, Button } from '@mui/material';
import {
  openSnackbarAdviseAtom,
  userBranchesAtom,
} from '../../../../../../Atoms';
import { genericFormAtom } from '../../../../../../store/genericFormStore';
import Dialog from '../../../../../../Components/Dialog';
import SchemaWrapper from '../../../../../../Components/engine/SchemaWrapper';
import {
  CenteredFlexBox,
  ColumnFlexBox,
} from '../../../../../../Components/FlexBox';
import formatSchemaPayload from '../../../../Utils/formatSchemaPayload';
import formatSchemaWithDefaultValues from '../../../../Utils/formatSchemaWithDefaultValues';
import groupBy from '../../../../../../Helpers/Arrays/groupBy';
import Header from '../../../../../../Components/ContactListWrapper/EntriesWrapper/Header';
import BackButton from '../../../BackButton';

const FiltersConfiguration = function FiltersConfiguration({
  show,
  onSubmit,
  branchId,
  tabs = [],
  filterParameters = [],
  close = () => {},
  id = null,
  backToSummary = false,
}) {
  const API = useAPI();
  const translate = useTranslate();
  const openSnackbar = useSetAtom(openSnackbarAdviseAtom);
  const [payload, setPayload] = useAtom(genericFormAtom);
  const userBranches = useAtomValue(userBranchesAtom);
  const [tabValue, setTabValue] = useState(0);
  const [waveFilters, setWaveFilters] = useState([]);

  const handleSubmit = useCallback(() => {
    if (onSubmit) {
      onSubmit({
        id,
        branchId,
        schema: formatSchemaPayload(payload, branchId),
      });
    }
  }, [payload, branchId, onSubmit]);

  useEffect(() => {
    if (!show) return;

    API.getWaveFilterBranches(
      branchId,
      (res) => {
        setWaveFilters(res);
      },
      () => {}
    );
  }, [show, branchId]);

  useEffect(() => {
    if (!tabs || tabs.length === 0) return;

    setTabValue(tabs[0]?.id || '');
  }, [tabs]);

  const handleChangeTab = (event, newTab) => {
    setTabValue(newTab);
  };

  const handleToggleFilter = (sectionId, filterId, onSuccess) => {
    API.toggleWaveFilterBranch(
      branchId,
      sectionId,
      filterId,
      () => {
        onSuccess();
      },
      () => {
        openSnackbar({
          severity: 'error',
          message: translate('ERROR_TOGGLE_FILTER'),
        });
      }
    );
  };

  return (
    <Fade in={show} unmountOnExit>
      <Paper
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          padding: '8px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Header
          caption={<BackButton onClick={close} label={'WAVE_CONFIGURATION'} />}
          title={`${translate('IMPORT_FILTERS')} - ${userBranches?.find(({ id }) => id === branchId)?.branchDescription || ''}`}
          hideSearchBar
          hideSorting
          hideFilters
          hideExportExcel
        />
        <Divider />
        <ColumnFlexBox overflow="auto" width="100%" height="100%">
          <Tabs
            variant="fullWidth"
            value={tabValue}
            onChange={handleChangeTab}
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark' ? '#3d3d3d' : 'background.paper',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            {tabs.map(({ id, name }) => (
              <Tab key={id} value={id} label={translate(name)} />
            ))}
          </Tabs>
          <SchemaWrapper
            onToggle={handleToggleFilter}
            dynamicFilters={groupBy(
              waveFilters?.filter(({ sectionId }) =>
                tabs.find(({ id }) => id === sectionId)
              ),
              'sectionId'
            )}
            filterParameters={
              filterParameters ??
              waveFilters?.map((wf) => ({
                ...wf,
                filtersParameters: wf?.storedProcedureParameters,
              }))
            }
            formatSchemaCallback={formatSchemaWithDefaultValues}
            tabValue={tabValue}
          />
        </ColumnFlexBox>
        <Divider />
        <CenteredFlexBox justifyContent="flex-end" padding="16px" gap="16px">
          {backToSummary && (
            <Button
              id="cancel-filters-configuration"
              onClick={backToSummary}
              variant="outlined"
              size="large"
            >
              {translate('BACK_TO_SUMMARY')}
            </Button>
          )}
          <Button
            id="submit-filters-configuration"
            size="large"
            sx={{ minWidth: '240px' }}
            onClick={handleSubmit}
            variant="contained"
          >
            {backToSummary
              ? translate('REAPPLY_FILTERS')
              : translate('RUN_SIMULATION')}
          </Button>
        </CenteredFlexBox>
      </Paper>
    </Fade>
  );
};

export default FiltersConfiguration;
